import * as React from "react";
import { get, useFormState } from "react-hook-form";

import {
  computeDefaultsOf,
  formatCompute,
  type ActionRecord,
} from "@/shared/domain/actions";
import { type TriggerTemplateCatalog } from "@/shared/domain/triggers";
import { FormBlock } from "@/shared/foundation/primitives";

import { AdvancedSettings } from "./AdvancedSettings";
import { ComputeFields } from "./ComputeFields";
import { InputDataBlock, type InputDataOffer } from "./InputDataBlock";
import { type TriggeringSubject } from "./inputSourceCopy";
import styles from "./invocationSpec.module.css";
import { OutputDestinationField } from "./OutputDestinationField";
import { parameterFormFor } from "./parameterForms/registry";
import { ParameterRow } from "./ParameterRow";

export interface InvocationSpecFieldsProps {
  /**
   * Where the invocation values live in the enclosing form: `"spec"` on a
   * manual invoke, `"targets.2.spec"` on a trigger target.
   */
  name: string;
  /** The chosen action, for its declared parameters. Picked outside the block. */
  action: ActionRecord | null;
  /**
   * Present when values may reference a firing: the paths on offer. Absent
   * where parameters are fixed values only.
   */
  templateCatalog?: TriggerTemplateCatalog;
  /**
   * Rendered at the top of "Input data": what the invocation is already
   * handed. Absent on a manual invoke.
   */
  inputDataHeader?: React.ReactNode;
  /** Sources the caller adds to the "Add input" menu. */
  inputDataOffers?: readonly InputDataOffer[];
  /**
   * Whether the invocation runs on a dataset its output can go back to. False
   * on a schedule, where the output dataset is optional.
   */
  hasSourceDataset: boolean;
  /**
   * What the firing is about, when its namespace carries a dataset that
   * "Input data" may offer. Absent on a manual invoke, which has no firing.
   */
  triggeringSubject?: TriggeringSubject;
  /**
   * A file name the invocation might run on, for an action form's previews.
   * The ingestion rule form derives one from its first path pattern.
   */
  exampleFileName?: string;
  /**
   * Every run starts on a file just uploaded, as on an ingestion rule, so an
   * action form leaves out settings about files already ingested.
   */
  startsOnUpload?: boolean;
}

function computeSummary(action: ActionRecord): string {
  const compute = computeDefaultsOf(action);
  return formatCompute({
    vCPU: Number(compute.cpu),
    memory: Number(compute.memory),
    storage: Number(compute.storage),
  });
}

/**
 * Everything about how to run a chosen action, as a form states it.
 *
 * Nothing here knows about triggers: it is handed the path it lives at, the
 * action, and whatever the caller wants said about the inputs the invocation
 * already has, and writes only the values under that path.
 */
export function InvocationSpecFields({
  name,
  action,
  templateCatalog,
  inputDataHeader,
  inputDataOffers,
  hasSourceDataset,
  triggeringSubject,
  exampleFileName,
  startsOnUpload = false,
}: InvocationSpecFieldsProps) {
  const parameters = action?.parameters ?? [];
  const { errors } = useFormState({ name: `${name}.parameters` });
  const parametersTitleId = React.useId();
  const resourcesTitleId = React.useId();
  const special = parameterFormFor(action?.name);

  return (
    <div className={styles.block} data-testid="invocation-spec">
      <InputDataBlock
        name={name}
        header={inputDataHeader}
        offers={inputDataOffers}
        triggeringSubject={triggeringSubject}
      />

      {special !== undefined && action !== null && (
        <FormBlock
          titleId={parametersTitleId}
          title="Action parameters"
          hint="A setting left alone follows the action’s own default when the invocation runs."
          data-testid="action-parameters"
          data-form={action.name}
        >
          <special.Form
            name={`${name}.parameters`}
            action={action}
            templateCatalog={templateCatalog}
            exampleFileName={exampleFileName}
            startsOnUpload={startsOnUpload}
          />
        </FormBlock>
      )}

      {special === undefined && parameters.length > 0 && (
        <FormBlock
          titleId={parametersTitleId}
          title="Action parameters"
          hint="A parameter left alone follows the action’s own default when the invocation runs."
          data-testid="action-parameters"
        >
          <div className={styles.group}>
            {parameters.map((parameter) => (
              <ParameterRow
                key={parameter.name}
                name={`${name}.parameters`}
                parameter={parameter}
                templateCatalog={templateCatalog}
                error={
                  (
                    get(errors, `${name}.parameters.${parameter.name}`) as
                      | { message?: string }
                      | undefined
                  )?.message
                }
              />
            ))}
          </div>
        </FormBlock>
      )}

      <OutputDestinationField name={name} hasSourceDataset={hasSourceDataset} />

      {special !== undefined && action !== null && (
        <FormBlock
          titleId={resourcesTitleId}
          title="Resources"
          hint={[
            `The action’s own: ${computeSummary(action)}.`,
            special.resourcesHint,
          ]
            .filter((line) => line !== undefined)
            .join(" ")}
          data-testid="resources"
        >
          <ComputeFields name={name} action={action} />
        </FormBlock>
      )}

      <AdvancedSettings
        name={name}
        action={action}
        hideCompute={special !== undefined}
      />
    </div>
  );
}
